import { Extension } from "@tiptap/core";
import { NodeSelection, Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";

/**
 * A click into text ends a node selection.
 *
 * Measured 2026-09-21 (Playwright, real Chromium): once a block node such as an
 * image is node-selected - by clicking it, or because a loaded document starts or
 * ends with it - a later click into a text paragraph leaves ProseMirror's
 * selection on that node, although the caret is visibly in the text. The next
 * keystrokes then replace the "selected" node: the image disappears from the
 * document without any message. It reproduces in a bare editor with only
 * StarterKit and Image (tiptap 3.30.2, prosemirror-view 1.42.1), so it is not
 * caused by this package's extension list. Any editor built from this list is exposed,
 * so the workaround is part of the list rather than of one element. Measured with
 * the same result: the horizontal rule (the only block-level atom StarterKit
 * ships) and taps on a touch screen. Measured as NOT affected, with or without
 * this extension: clicking a link after selecting an image, and a click that
 * moves the mouse a few pixels (ProseMirror then leaves the selection to the
 * browser, and that path updates it).
 *
 * When the click lands inside a textblock outside the selected node, the
 * selection is moved to the clicked position. Clicks on the selected node itself,
 * and clicks that do not land in text (for example on another image), are left
 * to ProseMirror's own handling, so selecting and deleting an image still works.
 */
export const ClickReleasesNodeSelection = Extension.create({
  name: "clickReleasesNodeSelection",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("clickReleasesNodeSelection"),
        props: {
          handleClick(view, pos) {
            const { selection, doc } = view.state;
            if (!(selection instanceof NodeSelection)) {
              return false;
            }
            if (pos >= selection.from && pos < selection.to) {
              return false;
            }
            const $pos = doc.resolve(pos);
            if (!$pos.parent.inlineContent) {
              return false;
            }
            view.dispatch(view.state.tr.setSelection(TextSelection.create(doc, pos)));
            // Not handled: ProseMirror still runs its own click behaviour afterwards.
            return false;
          },
        },
      }),
    ];
  },
});
